/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * MemoryConfigDialog
 * Extracted from AgentDetail. Edits memory policy + LLM/execution limits.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { AgentDetail as AgentDetailType } from '../../../types';

export interface MemoryConfigValues {
  short_term_capacity: number;
  long_term_capacity: number;
  auto_summarize: boolean;
  max_context_messages: number;
  keep_recent_messages: number;
  enable_pruning: boolean;
  enable_semantic_search: boolean;
  max_tokens: number;
  max_iterations: number;
  tool_result_max_chars: number;
  max_tool_context_messages: number;
}

const DEFAULTS: MemoryConfigValues = {
  short_term_capacity: 100,
  long_term_capacity: 1000,
  auto_summarize: true,
  max_context_messages: 20,
  keep_recent_messages: 10,
  enable_pruning: true,
  enable_semantic_search: true,
  max_tokens: 2000,
  max_iterations: 50,
  tool_result_max_chars: 2000,
  max_tool_context_messages: 20,
};

interface MemoryConfigDialogProps {
  open: boolean;
  agent: AgentDetailType;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: MemoryConfigValues) => void;
}

export const MemoryConfigDialog: React.FC<MemoryConfigDialogProps> = ({
  open,
  agent,
  saving,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<MemoryConfigValues>(DEFAULTS);

  useEffect(() => {
    if (!open) return;
    const c = agent.config || {};
    setForm({
      short_term_capacity: c.short_term_capacity ?? DEFAULTS.short_term_capacity,
      long_term_capacity: c.long_term_capacity ?? DEFAULTS.long_term_capacity,
      auto_summarize: c.auto_summarize !== false,
      max_context_messages: c.max_context_messages ?? DEFAULTS.max_context_messages,
      keep_recent_messages: c.keep_recent_messages ?? DEFAULTS.keep_recent_messages,
      enable_pruning: c.enable_pruning !== false,
      enable_semantic_search: c.enable_semantic_search !== false,
      max_tokens: c.max_tokens ?? DEFAULTS.max_tokens,
      max_iterations: c.max_iterations ?? DEFAULTS.max_iterations,
      tool_result_max_chars: c.tool_result_max_chars ?? DEFAULTS.tool_result_max_chars,
      max_tool_context_messages:
        c.max_tool_context_messages ?? DEFAULTS.max_tool_context_messages,
    });
  }, [open, agent]);

  const num = (key: keyof MemoryConfigValues, label: string, helper: string, min = 0, max = 100000) => (
    <TextField
      label={label}
      type="number"
      value={form[key] as number}
      onChange={(e) => setForm({ ...form, [key]: parseInt(e.target.value, 10) || 0 })}
      fullWidth
      helperText={helper}
      InputProps={{ inputProps: { min, max } }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Memory &amp; execution policy</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Memory capacity</Typography>
          {num('short_term_capacity', 'Short-term capacity', 'Recent conversation turns to remember', 10, 1000)}
          {num('long_term_capacity', 'Long-term capacity', 'Maximum persistent memories to store', 100, 10000)}

          <Divider />
          <Typography variant="subtitle2">Behaviour</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.auto_summarize}
                onChange={(e) => setForm({ ...form, auto_summarize: e.target.checked })}
              />
            }
            label="Auto-summarise long conversations"
          />
          {form.auto_summarize ? (
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {num('max_context_messages', 'Summarisation threshold', 'Messages before auto-summarisation triggers', 5, 100)}
              {num('keep_recent_messages', 'Recent messages to keep', 'Sliding window size after summarisation', 2, 50)}
            </Box>
          ) : null}

          <FormControlLabel
            control={
              <Switch
                checked={form.enable_pruning}
                onChange={(e) => setForm({ ...form, enable_pruning: e.target.checked })}
              />
            }
            label="Prune low-importance memories when capacity is reached"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.enable_semantic_search}
                onChange={(e) => setForm({ ...form, enable_semantic_search: e.target.checked })}
              />
            }
            label="Use semantic search to find relevant memories"
          />

          <Divider />
          <Typography variant="subtitle2">LLM &amp; execution limits</Typography>
          {num('max_tokens', 'Max output tokens', 'Maximum tokens the LLM can generate per response', 100, 128000)}
          {num('max_iterations', 'Max iterations', 'Tool-calling iterations before stopping (prevents loops)', 1, 100)}
          {num('tool_result_max_chars', 'Tool result max chars', 'Truncation limit for tool results (prevents context overflow)', 500, 20000)}
          {num('max_tool_context_messages', 'Max tool context messages', 'Tool messages kept in context per execution', 5, 50)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)} disabled={saving}>
          Save settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemoryConfigDialog;
